import React, { useEffect, useState, Component } from 'react';
import ReactDOM from 'react-dom';
import { X, ZoomIn } from 'lucide-react';
interface LightboxImageProps {
  src: string;
  alt: string;
  label?: string;
  className?: string;
}
// Modal Portal Component
function ModalPortal({
  children,
  isOpen
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  if (!mounted || !isOpen) return null;
  return ReactDOM.createPortal(children, document.body);
}
export function LightboxImage({
  src,
  alt,
  label,
  className = ''
}: LightboxImageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  // Handle escape key and body scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '15px';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);
  const closeModal = () => setIsOpen(false);
  return <>
      <div className={`relative group cursor-pointer overflow-hidden rounded-lg ${className}`} onClick={() => setIsOpen(true)} data-id="element-325">
        <img src={src} alt={alt} loading="eager" decoding="async" onLoad={() => setIsLoaded(true)} className="w-full h-auto md:group-hover:scale-[1.02] transition-transform duration-300" data-id="element-326" />
        <div className="absolute inset-0 bg-black/0 md:group-hover:bg-black/30 flex items-center justify-center transition-all duration-200" data-id="element-327">
          <div className="opacity-0 md:group-hover:opacity-100 transform translate-y-2 md:group-hover:translate-y-0 transition-all duration-200" data-id="element-328">
            <div className="bg-white text-gray-900 px-5 py-2.5 rounded-full text-sm font-medium shadow-lg flex items-center gap-2" data-id="element-329">
              <ZoomIn className="w-4 h-4" data-id="element-330" />
              Click to enlarge
            </div>
          </div>
        </div>
      </div>

      {/* Modal rendered via Portal */}
      <ModalPortal isOpen={isOpen} data-id="element-331">
        <div onClick={closeModal} style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }} data-id="element-332">
          {/* Close Button */}
          <button onClick={e => {
          e.stopPropagation();
          closeModal();
        }} style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'rgba(255,255,255,0.2)',
          border: 'none',
          borderRadius: '50%',
          padding: '12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s'
        }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'} onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'} aria-label="Close" data-id="element-333">
            <X style={{
            width: '32px',
            height: '32px',
            color: 'white'
          }} data-id="element-334" />
          </button>

          {/* Label */}
          {label && <div style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          color: 'white',
          fontSize: '18px',
          fontWeight: 500
        }} data-id="element-335">
              {label}
            </div>}

          {/* Image */}
          <img src={src} alt={alt} onClick={e => e.stopPropagation()} style={{
          maxWidth: '90vw',
          maxHeight: '85vh',
          objectFit: 'contain',
          borderRadius: '8px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }} data-id="element-336" />
        </div>
      </ModalPortal>
    </>;
}